import React from 'react';

import { Alert, FormControlLabel, Paper, Radio, RadioGroup, Stack, Typography } from '@mui/material';

import type { EgsRatePlan } from './egsRatePlans';

interface Props {
  onSelectRatePlan: (ratePlanId: string) => void;
  ratePlanId?: string;
  ratePlans: Array<EgsRatePlan>;
}

/**
 * Salesforce has no rate plan field, so a surcharge row cannot say which plan it prices. Asked once here
 * rather than again on every room, and every later step is scoped to the answer.
 */
export default function EgsRatePlanStep({ onSelectRatePlan, ratePlanId, ratePlans }: Props) {
  if (ratePlans.length === 0) {
    return <Alert severity="info">This offer has no room rates, so there is no rate plan to write to.</Alert>;
  }

  return (
    <Stack spacing={1}>
      <RadioGroup onChange={(event) => onSelectRatePlan(event.target.value)} value={ratePlanId ?? ''}>
        <Stack spacing={1}>
          {ratePlans.map((ratePlan) => (
            <Paper key={ratePlan.id} sx={{ px: 2, py: 1 }} variant="outlined">
              <FormControlLabel
                control={<Radio size="small" />}
                label={
                  <Stack>
                    <Typography variant="body2">{ratePlan.name}</Typography>
                    <Typography color="text.secondary" variant="caption">
                      {`${ratePlan.roomRateCount} room ${ratePlan.roomRateCount === 1 ? 'rate' : 'rates'}`}
                    </Typography>
                  </Stack>
                }
                sx={{ alignItems: 'center', m: 0, width: '100%' }}
                value={ratePlan.id}
              />
            </Paper>
          ))}
        </Stack>
      </RadioGroup>

      {/* Otherwise a CM expecting one pass to cover the offer reads the short room list as missing data. */}
      {ratePlans.length > 1 && (
        <Alert severity="info">
          Only room rates on the plan you pick are written. Run this again for the other plans.
        </Alert>
      )}
    </Stack>
  );
}
